import { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { createColumnHelper } from "@tanstack/react-table";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  Cell,
} from "recharts";
import PageHeader from "../components/layout/PageHeader";
import KpiCard from "../components/ui/KpiCard";
import ChartCard from "../components/ui/ChartCard";
import ProgressBar from "../components/ui/ProgressBar";
import StatusBadge from "../components/ui/StatusBadge";
import DataTable from "../components/ui/DataTable";
import { MoneyTooltip } from "../components/ui/MoneyTooltip";
import { EmptyState, ErrorState, KpiSkeletonRow, CardSkeleton } from "../components/ui/states";
import { useApi } from "../lib/useApi";
import { qs } from "../lib/api";
import { useAuth } from "../auth/AuthContext";
import { formatUSD, formatPct, formatDate } from "../lib/format";
import { CHART } from "../lib/chart";
import { isFinancing, SETTLED } from "../lib/recon";
import type { PortfolioSummary, ProjectBudgetRow, ReconciliationResponse } from "@shared/types";

const col = createColumnHelper<ProjectBudgetRow>();

export default function ExecutiveSummary() {
  const { activeClientId } = useAuth();
  const navigate = useNavigate();
  const { data, loading, error, refetch } = useApi<PortfolioSummary>(
    `/api/portfolio/summary${qs({ clientId: activeClientId })}`
  );
  const recon = useApi<ReconciliationResponse>(`/api/reconciliation${qs({ clientId: activeClientId })}`);

  // Reconciliation aggregate split into expenses (Source_Type = Expense) vs
  // financing (Source_Type = Financing). Expense movements are the ones the
  // client reconciles against receipts; financing movements are settled by
  // design. Computed client-side from the ledger so it matches Reconciliation.
  const recAgg = useMemo(() => {
    const items = recon.data?.items ?? [];
    let expReconciled = 0; // settled expense movements (Reconciled / no-receipt)
    let expReview = 0; // expense movements still needing review
    let expReviewCount = 0;
    let financing = 0; // settled financing movements
    let financingCount = 0;
    let grandTotal = 0;
    for (const it of items) {
      const amt = Math.abs(it.amount ?? 0);
      grandTotal += amt;
      const isFin = it.sourceType === "Financing";
      if (isFin) {
        financing += amt;
        financingCount += 1;
      } else if (SETTLED.has(it.matchStatus)) {
        expReconciled += amt;
      } else {
        expReview += amt;
        expReviewCount += 1;
      }
    }
    const expenseTotal = expReconciled + expReview;
    const totalReconciledInclFinancing = expReconciled + financing;
    return {
      expReconciled,
      expReview,
      expReviewCount,
      expenseTotal,
      financing,
      financingCount,
      totalReconciledInclFinancing,
      grandTotal,
      count: items.length,
    };
  }, [recon.data]);

  // Financing movements grouped by project, split by SOV-stage coverage.
  const financingByProject = useMemo(() => {
    const map = new Map<string, { name: string; withSov: number; withoutSov: number; total: number }>();
    for (const it of recon.data?.items ?? []) {
      if (it.sourceType !== "Financing") continue;
      const name = it.projectName ?? "Unassigned";
      const amt = Math.abs(it.amount ?? 0);
      const e = map.get(name) ?? { name, withSov: 0, withoutSov: 0, total: 0 };
      if (it.sovStage) e.withSov += amt;
      else e.withoutSov += amt;
      e.total += amt;
      map.set(name, e);
    }
    return [...map.values()].sort((a, b) => b.total - a.total);
  }, [recon.data]);

  const financingRows = useMemo(
    () => (recon.data?.items ?? []).filter((i) => isFinancing(i.matchStatus)),
    [recon.data]
  );

  const columns = useMemo(
    () => [
      col.accessor("name", {
        header: "Project",
        cell: (c) => <span className="font-medium text-ink-900">{c.getValue()}</span>,
      }),
      col.accessor("status", {
        header: "Status",
        cell: (c) => (c.getValue() ? <StatusBadge status={c.getValue()} /> : <span className="text-ink-300">—</span>),
      }),
      col.accessor("budget", {
        header: "Budget",
        meta: { numeric: true },
        cell: (c) => formatUSD(c.getValue()),
      }),
      col.accessor("spent", {
        header: "Spent",
        meta: { numeric: true },
        cell: (c) => formatUSD(c.getValue()),
      }),
      col.accessor("utilizationPct", {
        header: "Utilization",
        cell: (c) => <div className="w-40"><ProgressBar ratio={c.getValue()} /></div>,
      }),
      col.accessor("activeFlags", {
        header: "Flags",
        meta: { numeric: true },
        cell: (c) => {
          const n = c.getValue();
          const sev = c.row.original.topSeverity;
          if (!n) return <span className="text-ink-300">—</span>;
          const cls =
            sev === "critical" ? "bg-critical-50 text-critical-700" : sev === "warning" ? "bg-warn-50 text-warn-700" : "bg-accent-50 text-accent-700";
          return <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${cls}`}>{n}</span>;
        },
      }),
    ],
    []
  );

  if (loading) {
    return (
      <>
        <PageHeader title="Executive Summary" subtitle="Reconciliation status and budget utilization across all projects." />
        <KpiSkeletonRow />
        <div className="mt-4 grid gap-4 lg:grid-cols-2">
          <CardSkeleton rows={5} />
          <CardSkeleton rows={5} />
        </div>
      </>
    );
  }
  if (error) {
    return (
      <>
        <PageHeader title="Executive Summary" />
        <ErrorState message={error} onRetry={refetch} />
      </>
    );
  }
  if (!data || data.projects.length === 0) {
    return (
      <>
        <PageHeader title="Executive Summary" />
        <EmptyState title="No projects" message="There are no projects in the selected scope." />
      </>
    );
  }

  const k = data.kpis;
  const statusSummary =
    k.projectsByStatus
      .filter((s) => s.status)
      .map((s) => `${s.count} ${s.status}`)
      .join(" · ") || `${k.projectCount} project${k.projectCount === 1 ? "" : "s"}`;

  const r = recAgg;
  const expRecPct = r.expenseTotal > 0 ? r.expReconciled / r.expenseTotal : 0;
  // Sanity invariant: the three buckets must add up to the grand total of every
  // movement. They are computed in one pass, so any drift indicates a bug.
  const totalsBalance = Math.abs(r.expReconciled + r.expReview + r.financing - r.grandTotal) < 0.01;
  const expenseSplit = [
    { name: "Reconciled", value: r.expReconciled, fill: CHART.positive },
    { name: "Needs review", value: r.expReview, fill: CHART.warn },
  ];

  return (
    <>
      <PageHeader
        title="Executive Summary"
        subtitle="Reconciliation status and budget utilization across all projects."
      />

      {/* KPI row */}
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-3">
        <KpiCard
          label="Reconciled (expenses)"
          value={formatUSD(r.expReconciled, { compact: true })}
          accent="positive"
          sub={recon.loading ? "Loading…" : `${formatPct(expRecPct)} of expenses`}
        />
        <KpiCard
          label="Total reconciled (incl. financing)"
          value={formatUSD(r.totalReconciledInclFinancing, { compact: true })}
          accent="positive"
          sub={`incl. ${formatUSD(r.financing, { compact: true })} financing`}
        />
        <KpiCard
          label="Total movements"
          value={formatUSD(r.grandTotal, { compact: true })}
          accent={totalsBalance ? undefined : "critical"}
          sub={totalsBalance ? `${r.count} movement${r.count === 1 ? "" : "s"}` : "⚠ totals do not balance"}
        />
        <KpiCard
          label="Needs review"
          value={formatUSD(r.expReview, { compact: true })}
          accent={r.expReviewCount > 0 ? "warn" : "positive"}
          sub={`${r.expReviewCount} expense movement${r.expReviewCount === 1 ? "" : "s"} to reconcile`}
        />
        <KpiCard
          label="Budget Utilization"
          value={formatPct(k.budgetUtilizationPct)}
          accent={k.budgetUtilizationPct > 1 ? "critical" : k.budgetUtilizationPct >= 0.9 ? "warn" : "positive"}
          sub={`${formatUSD(k.budgetSpent, { compact: true })} of ${formatUSD(k.budgetTotal, { compact: true })}`}
        />
        <KpiCard label="Projects" value={k.projectCount} sub={statusSummary} />
      </div>

      <div className="mt-4 grid gap-4 lg:grid-cols-2">
        {/* Reconciled vs unreconciled — expenses only */}
        <ChartCard title="Reconciled vs unreconciled" subtitle="Expense movements only (financing excluded)">
          {r.expenseTotal === 0 ? (
            <EmptyState title="No expense movements" message="No expense movements in this scope." icon="⇄" />
          ) : (
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={expenseSplit} margin={{ left: 8, right: 8, top: 8 }}>
                <CartesianGrid strokeDasharray="3 3" stroke={CHART.grid} vertical={false} />
                <XAxis dataKey="name" tick={CHART.tick} stroke={CHART.axis} />
                <YAxis tickFormatter={(v) => formatUSD(v, { compact: true })} tick={CHART.tick} stroke={CHART.axis} width={56} />
                <Tooltip content={<MoneyTooltip />} />
                <Bar dataKey="value" name="Amount" radius={[2, 2, 0, 0]}>
                  {expenseSplit.map((d) => (
                    <Cell key={d.name} fill={d.fill} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          )}
        </ChartCard>

        {/* Financing by project + SOV coverage */}
        <ChartCard title="Financing by project" subtitle="Financing movements grouped by project, split by SOV-stage coverage">
          {financingByProject.length === 0 ? (
            <EmptyState title="No financing movements" message="No financing movements in this scope." icon="$" />
          ) : (
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={financingByProject} margin={{ left: 8, right: 8, top: 8 }}>
                <CartesianGrid strokeDasharray="3 3" stroke={CHART.grid} vertical={false} />
                <XAxis dataKey="name" tick={CHART.tickSmall} stroke={CHART.axis} interval={0} />
                <YAxis tickFormatter={(v) => formatUSD(v, { compact: true })} tick={CHART.tick} stroke={CHART.axis} width={56} />
                <Tooltip content={<MoneyTooltip />} />
                <Legend wrapperStyle={CHART.legend} />
                <Bar dataKey="withSov" name="SOV assigned" stackId="f" fill={CHART.accent} radius={[0, 0, 0, 0]} />
                <Bar dataKey="withoutSov" name="No SOV stage" stackId="f" fill={CHART.axis} radius={[2, 2, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </ChartCard>
      </div>

      {/* Financial movements (intentionally not reconciled) */}
      {financingRows.length > 0 && (
        <ChartCard
          title={`Financial movements (${financingRows.length})`}
          subtitle="Financing movements with no receipt — settled by design, not pending reconciliation"
          className="mt-4"
        >
          <div className="max-h-80 overflow-auto rounded-lg border border-ink-200">
            <table className="w-full text-sm">
              <thead className="sticky top-0 z-10 bg-ink-50 text-left text-xs uppercase tracking-wide text-ink-500">
                <tr>
                  <th className="px-3 py-2 font-medium">Date</th>
                  <th className="px-3 py-2 font-medium">Counterparty</th>
                  <th className="px-3 py-2 font-medium">Project</th>
                  <th className="px-3 py-2 font-medium">Category</th>
                  <th className="px-3 py-2 text-right font-medium">Amount</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-ink-100 bg-white">
                {financingRows.map((i) => (
                  <tr key={i.id}>
                    <td className="whitespace-nowrap px-3 py-2 text-ink-600">{formatDate(i.date)}</td>
                    <td className="px-3 py-2 text-ink-900">{i.counterparty ?? "—"}</td>
                    <td className="px-3 py-2 text-ink-700">{i.projectName ?? "—"}</td>
                    <td className="px-3 py-2 text-ink-700">{i.sovStage ?? "—"}</td>
                    <td className="whitespace-nowrap px-3 py-2 text-right tabular-nums text-ink-900">{formatUSD(i.amount)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </ChartCard>
      )}

      {/* Global project table */}
      <div className="mt-4">
        <h3 className="mb-2 text-sm font-semibold text-ink-800">All projects</h3>
        <DataTable
          data={data.projects}
          columns={columns}
          stickyFirstColumn
          onRowClick={(p) => navigate(`/cash-flow/${p.id}`)}
          initialSort={[{ id: "budget", desc: true }]}
        />
        <p className="mt-2 text-xs text-ink-400">Click a row to drill into project cash flow.</p>
      </div>
    </>
  );
}
